import React from 'react';
import { gql, useQuery } from '@apollo/client';
// Params are provided from Rails via ReactOnRails props
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import Button from '../../../../ui/canon/Button.jsx';
import CoachChip from '../../components/coachLed/CoachChip.jsx';
import CurriculumPreview from '../../components/coachLed/CurriculumPreview.jsx';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const PROGRAM_QUERY = gql`
  query SprintProgram($slug: String) {
    sprintProgram(slug: $slug) {
      id
      title
      slug
      summary
      level
      price_cents
      duration_days
      featured
      hero_image_url
      goals
      avg_rating
      enrollment_count
      is_group
      benefits
      coachProfile { id name headline bio avatar_url verified rating review_count }
      taskTemplates { id title description_md estimated_minutes requires_attachment day_offset canonical_task_type }
    }
  }
`;

export default function ProgramDetailContainer(props) {
  const { slug } = props || {};
  const { data, loading, error } = useQuery(PROGRAM_QUERY, { variables: { slug } });
  const p = data?.sprintProgram;

  if (loading) return <SingleColumnTemplate><div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 text-succession-text-primary">Loading...</div></SingleColumnTemplate>;
  if (error || !p) return <SingleColumnTemplate><div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 text-succession-text-primary">Program not found</div></SingleColumnTemplate>;

  return (
    <SingleColumnTemplate title={p.title} subtitle={p.summary} variant="wide">
      {p.hero_image_url && (
        <div className="rounded-2xl overflow-hidden border border-succession-border bg-succession-bg-secondary">
          <img src={p.hero_image_url} alt={p.title} className="w-full h-72 object-cover" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-8">
            <h2 className="text-2xl font-bold mb-4 text-succession-text-primary">Your Coach</h2>
            <CoachChip coach={p.coachProfile} showBio />
          </div>

          {/* What You'll Achieve Section */}
          {p.benefits && p.benefits.length > 0 && (
            <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-8">
              <h2 className="text-2xl font-bold mb-4 text-succession-text-primary">What You'll Achieve</h2>
              <ul className="space-y-3">
                {p.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-succession-success flex-shrink-0 mt-0.5" />
                    <span className="text-succession-text-primary">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-8">
            <h2 className="text-2xl font-bold mb-4 text-succession-text-primary">Program Curriculum</h2>
            <p className="text-succession-text-secondary mb-4">
              This {p.duration_days}-day program includes {p.taskTemplates.length} structured tasks designed to guide you step-by-step.
            </p>
            <CurriculumPreview templates={p.taskTemplates} />
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 space-y-4 sticky top-6">
            <div>
              <div className="text-3xl font-bold mb-1 text-succession-text-primary">{p.price_cents ? `$${(p.price_cents/100).toLocaleString()}` : 'Free'}</div>
              <p className="text-sm text-succession-text-secondary">One-time payment • {p.duration_days} days</p>
              <div className="mt-2 text-sm">
                {typeof p.avg_rating === 'number' ? (
                  <span className="inline-flex items-center gap-1 text-succession-text-secondary">
                    <span className="text-yellow-500">★</span>{Number(p.avg_rating).toFixed(1)} • {p.enrollment_count || 0} enrollments
                  </span>
                ) : (
                  <span className="text-succession-text-secondary">{p.enrollment_count || 0} enrollments</span>
                )}
              </div>
            </div>
            <Button
              as="a"
              href={`/sprints/programs/${p.slug}/enroll`}
              variant="primary"
              size="lg"
              className="w-full"
              aria-label={`Start ${p.is_group ? 'cohort' : 'one on one'} sprint`}
              onClick={() => {
                try {
                  if (window.ahoy && typeof window.ahoy.track === 'function') {
                    window.ahoy.track('program_enroll_click', { slug: p.slug, program_id: p.id });
                  }
                } catch (_) {}
              }}
            >
              Start {p.is_group ? 'Cohort' : '1:1 Sprint'}
            </Button>
            <Button
              as="a"
              href={`/coaches/${p.coachProfile?.id || ''}`}
              variant="outline"
              size="lg"
              className="w-full"
              aria-label="Message coach"
              onClick={() => {
                try {
                  if (window.ahoy && typeof window.ahoy.track === 'function') {
                    window.ahoy.track('message_coach_click', { coach_id: p.coachProfile?.id, program_id: p.id });
                  }
                } catch (_) {}
              }}
            >
              Message Coach
            </Button>
            <div className="pt-4 border-t border-succession-border text-center text-sm text-succession-text-secondary">
              <p>✓ Secure payment</p>
              <p>✓ 30-day money-back guarantee</p>
            </div>
          </div>
        </div>
      </div>
    </SingleColumnTemplate>
  );
}


